import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { IconTextLine } from '@components/molecules/icon-text-line/icon-text-line';
import { cn } from '@utils';

export enum BannerVariantEnum {
	INFO = 'info',
	WARNING = 'warning',
	ERROR = 'error',
	HIGHLIGHT = 'highlight',
}

const bannerVariants = cva('flex w-full rounded-md border px-4 py-3', {
	variants: {
		variant: {
			[BannerVariantEnum.INFO]: 'rounded-lg border-borderPrimary bg-bgPrimary text-primary',
			[BannerVariantEnum.WARNING]: 'rounded-lg border-warning/40 bg-warning/10 text-warning',
			[BannerVariantEnum.ERROR]: 'rounded-lg border-error/40 bg-error/10 text-error',
			[BannerVariantEnum.HIGHLIGHT]: 'rounded-lg border-borderPrimary bg-bgPrimary text-primary',
		},
	},
	defaultVariants: {
		variant: BannerVariantEnum.INFO,
	},
});

type BannerProps = {
	icon?: React.ReactNode;
	variant?: BannerVariantEnum;
	className?: string;
	iconClassName?: string;
	contentClassName?: string;
	'data-testid'?: string;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'color'> &
	VariantProps<typeof bannerVariants>;

export const Banner: React.FC<BannerProps> = ({
	icon,
	variant = BannerVariantEnum.INFO,
	className,
	iconClassName,
	contentClassName,
	children,
	...props
}) => {
	return (
		<div className={cn(bannerVariants({ variant }), className)} data-testid={props['data-testid']} {...props}>
			{icon ? (
				<IconTextLine
					className="w-full gap-2"
					icon={<span className={cn('flex shrink-0 items-center [&>svg]:size-5', iconClassName)}>{icon}</span>}
				>
					<div className={cn('text-sm', contentClassName)}>{children}</div>
				</IconTextLine>
			) : (
				<div className={cn('w-full', contentClassName)}>{children}</div>
			)}
		</div>
	);
};
